import React from 'react'

import { PDFDocument } from 'pdf-lib'
import QRCode from 'qrcode'

import {
  sortTickets,
  formatNumber,
  formatDateTime,
  getTimezoneDate,
  timezones,
  getAccessibleLabel,
} from '../../utilities/helpers'
import {
  addBrandedTicketPage,
  createTicketPdfFonts,
  formatTicketHolderName,
} from '../../utilities/ticketPdfTemplate'

import Alert from 'react-bootstrap/Alert'
import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { ActionBtns } from '../ActionBtns'

import scanQR from '../../assets/icons/scan-qr-code.svg'

export default function MyTickets({
  eventObj,
  order,
  guestList,
  handleClick,
  ticketStatus,
}) {
  const tickets = order ? order?.tickets : guestList?.guest_passes
  const event = eventObj || order?.event

  const timezone = timezones(event?.venue?.timezone)
  const canPrintTickets = event?.organization?.setting?.printTickets === true

  const generatePDF = async (ticket, order) => {
    const pdfDoc = await PDFDocument.create()
    const fonts = await createTicketPdfFonts(pdfDoc)
    const organization = event?.organization || order?.organization
    const zonedDate = getTimezoneDate(event?.start, timezone)
    const ticketHolder = formatTicketHolderName({
      firstName: order?.firstName,
      lastName: order?.lastName,
      email: order?.email,
      name: order?.users_permissions_user
        ? `${order.users_permissions_user.firstName || ''} ${
            order.users_permissions_user.lastName || ''
          }`.trim()
        : undefined,
    })

    const qrDataUrl = await QRCode.toDataURL(ticket?.checkInCode)
    const qrImage = await pdfDoc.embedPng(qrDataUrl)
    await addBrandedTicketPage(pdfDoc, {
      ticket,
      event,
      organization,
      purchasedByOrganizationName: order?.purchasedByOrganizationName,
      fonts,
      qrImage,
      ticketHolder,
      packageName: order?.package?.name,
      dateLabel: formatDateTime(zonedDate, 'dateOnly'),
      timeLabel: formatDateTime(zonedDate, 'timeOnly'),
    })

    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    window.open(url, '_blank')
  }

  const getTitle = () => {
    let title
    if (order) {
      title = `${tickets?.length > 1 ? 'Tickets' : 'Ticket'}`
    } else if (guestList) {
      title = `Guest ${tickets?.length > 1 ? 'passes' : 'pass'}`
    }

    return title
  }

  return (
    <section>
      <div className="section-heading-md heading--flex">
        <h2>
          {getTitle()} ({formatNumber(tickets.length)})
        </h2>
        <ActionBtns
          handleClick={handleClick}
          ticketStatus={ticketStatus}
          order={order}
          eventObj={event}
        />
      </div>
      <Alert variant="info">
        <img src={scanQR} width="44" height="44" alt="" />
        <div>
          <p className="fw-semi-bold">Your phone is your ticket</p>
          <p className="caption">
            Login to blocktickets.xyz on your phone and go to My events and then
            click Event details.
          </p>
        </div>
      </Alert>
      <Stack gap={3} className="mt-4">
        {sortTickets(tickets).map((ticket, index) => (
          <Card body className="card--white" key={index}>
            <div className="heading--flex">
              <Card.Title as="h5" className="card-title-sm">
                {ticket?.generalAdmission ? (
                  <>{ticket?.sectionNumber}</>
                ) : (
                  <Stack direction="horizontal" gap={4}>
                    <Stack direction="horizontal" gap={2}>
                      <span className="text-muted normal fw-normal">Sec</span>
                      <span>{ticket?.sectionNumber}</span>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                      <span className="text-muted normal fw-normal">Row</span>
                      <span>{ticket?.rowNumber}</span>
                    </Stack>
                    <Stack direction="horizontal" gap={2}>
                      <span className="text-muted normal fw-normal">Seat</span>
                      <span>{ticket?.seatNumber}</span>
                    </Stack>
                  </Stack>
                )}
              </Card.Title>
              <Stack direction="horizontal" gap={3}>
                <Button
                  onClick={() => handleClick('details', ticket)}
                  variant="link"
                >
                  Details
                </Button>
                {canPrintTickets && (
                  <Button
                    size="xs"
                    className="btn-print btn-print--sm icon-button-xs icon-button--white"
                    onClick={() => generatePDF(ticket, order)}
                  >
                    Print
                  </Button>
                )}
              </Stack>
            </div>
            {ticket?.accessible && (
              <div className="accessible accessible-sm accessible-label">
                <span>{getAccessibleLabel(ticket?.accessibleType)}</span>
              </div>
            )}
            {ticket?.additionalLabel && (
              <div className="text-muted caption mt-2">
                {ticket.additionalLabel}
              </div>
            )}
          </Card>
        ))}
      </Stack>
    </section>
  )
}
